import Header from './Header';
import Sidebar from './Sidebar'
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { fetchUserDetails, setUserData, openTermsModal, closeTermsModal } from '@/store/feature/UserSlice';
import { DashboardService } from '@/services/Dashboard/DashboardService';
import { ThrowError } from '@/api/callApi';
import TermsConditionsModal from '@/components/common/TermsConditionsModal';

interface LayoutProps {
    children: React.ReactNode;
}


const Layout: React.FC<LayoutProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const userDetail = useAppSelector((state) => state.user.user);
    const userDetailsLoading = useAppSelector((state) => state.user.userDetailsLoading);
    const isTermsModalOpen = useAppSelector((state) => state.user.isTermsModalOpen);

    const isProfileLoaded = userDetailsLoading === 'succeeded';
    const tandcAccepted = Boolean(userDetail?.tandcAccepted);


    const shouldShowTermsGate = Boolean(userDetail) && isProfileLoaded && !tandcAccepted;

    const [acceptingTerms, setAcceptingTerms] = useState(false);

    useEffect(() => {
        dispatch(fetchUserDetails());
    }, [dispatch]);


    useEffect(() => {
        if (shouldShowTermsGate) {
            dispatch(openTermsModal());
        }
    }, [shouldShowTermsGate, dispatch]);

    const handleAcceptTerms = async () => {
        if (!userDetail?.id) return;
        setAcceptingTerms(true);
        try {
            const payload = {
                tandcAccepted: true,
            };
            await DashboardService.editProfile(userDetail.id, payload);

            dispatch(setUserData({ ...userDetail, tandcAccepted: true }));
            dispatch(fetchUserDetails());
            dispatch(closeTermsModal());
        } catch (err) {
            ThrowError(err);
        } finally {
            setAcceptingTerms(false);
        }
    };

    const [isCollapsed, setIsCollapsed] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('sidebarCollapsed');
            return saved === 'true';
        }
        return false;
    });

    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const toggleSidebar = () => {
        setIsCollapsed(prev => {
            const newState = !prev;
            localStorage.setItem('sidebarCollapsed', String(newState));
            return newState;
        });
    };

    const toggleMobileSidebar = () => {
        setIsMobileOpen(prev => !prev);
    };

    return (
        <div className='min-h-screen bg-[#f1f1f1]'>
            <Sidebar
                isCollapsed={isCollapsed}
                toggleSidebar={toggleSidebar}
                isMobileOpen={isMobileOpen}
                toggleMobileSidebar={toggleMobileSidebar}
            />
            <div className={`transition-all duration-100 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-72'} min-h-screen flex flex-col`}>
                <Header
                    isCollapsed={isCollapsed}
                    toggleSidebar={toggleSidebar}
                    toggleMobileSidebar={toggleMobileSidebar}
                />
                <main className="flex-1 px-3 sm:px-6 py-6 space-y-6 overflow-x-hidden relative">
                    <div className="absolute top-0 left-0 right-0 h-64 bg-linear-to-b from-blue-50/50 to-transparent pointer-events-none" />
                    <div
                        className={`relative z-10 transition-all duration-300 ${shouldShowTermsGate ? 'blur-sm pointer-events-none select-none' : ''
                            }`}
                    >
                        {children}
                    </div>
                </main>
            </div>


            {shouldShowTermsGate && !isTermsModalOpen && (
                <button
                    onClick={() => dispatch(openTermsModal())}
                    className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold shadow-lg shadow-primary/40 hover:brightness-110 transition-all cursor-pointer"
                >
                    Review Terms & Conditions
                </button>
            )}

            {userDetail && (
                <TermsConditionsModal
                    open={isTermsModalOpen && shouldShowTermsGate}
                    onClose={() => dispatch(closeTermsModal())}
                    onAccept={handleAcceptTerms}
                    loading={acceptingTerms}
                />
            )}


        </div>
    )
}

export default Layout